import React, {useEffect, useRef} from "react";
import * as d3 from "d3";
import {OfficeVisit} from "../../api/Model";
import {stringCmp} from "../../util/SortUtil";

const weekendFlexDeskColor = "#D0D0D0";

const problemFlexColor = "#FF0000";
const problemAssignedColor = "#CC0000";

const basicFlexColor = "#808080";
const basicAssignedColor = "#666666";

export function VisitChart({visits, deskCount, recommendedAssignedDeskCount, columnWidth = 10}: {
    visits: OfficeVisit[],
    deskCount: number,
    recommendedAssignedDeskCount?: number,
    columnWidth?: number,
}) {
    const svgRef = useRef(null);

    useEffect(() => {
        if (!visits || visits.length === 0) {
            return;
        }

        // Clear previous SVG content
        d3.select(svgRef.current).selectAll("*").remove();

        // Set dimensions and margins
        const margin = {top: 30, right: 80, bottom: 70, left: 60};
        const width = visits.length * columnWidth;
        const height = 400 - margin.top - margin.bottom;

        // Create SVG
        const svg = d3.select(svgRef.current)
            .attr("width", width + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom)
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // Parse dates and sort visits by date
        const sortedVisits = [...visits]
            .sort(stringCmp('date'));

        // Create scales
        const x = d3
            .scaleBand()
            .domain(sortedVisits.map(d => d.date))
            .range([0, width])
            .padding(0.2);

        const maxVisitorCount = Math.max(d3.max(sortedVisits, d => d.visitorCount) || 0, deskCount);
        const y = d3.scaleLinear()
            .domain([0, maxVisitorCount * 1.1]) // Add 10% padding at the top
            .range([height, 0]);

        function countAssigned(d: OfficeVisit) {
            return d.type === 'weekend' ? 0 : (recommendedAssignedDeskCount ?? 0);
        }

        // Add X axis
        svg.append("g")
            .attr("transform", `translate(0,${height})`)
            .call(d3.axisBottom(x))
            .selectAll("text")
            .style("text-anchor", "end")
            .attr("dx", "-.8em")
            .attr("dy", ".15em")
            .attr("transform", "rotate(-65)")
            .style("display", value => {
                // Only show labels for Monday dates
                const date = new Date(String(value));
                return date.getDay() === 1 ? "block" : "none"; // 1 is Monday in JavaScript's getDay()
            });

        // Add Y axis
        svg.append("g")
            .call(d3.axisLeft(y));

        // Add Y axis label
        svg.append("text")
            .attr("transform", "rotate(-90)")
            .attr("y", 0 - margin.left)
            .attr("x", 0 - (height / 2))
            .attr("dy", "1em")
            .style("text-anchor", "middle")
            .text("Number of Visitors");

        // Create bars
        // Lower part (recommended assigned desk count)
        if (recommendedAssignedDeskCount) {
            svg.selectAll(".assigned-desk-rect")
                .data(sortedVisits)
                .enter()
                .append("rect")
                .attr("x", d => x(d.date) || 0)
                .attr("y", d => y(countAssigned(d)))
                .attr("width", x.bandwidth())
                .attr("height", d => height - y(countAssigned(d)))
                .attr("fill", d => d.visitorCount > deskCount ? problemAssignedColor : basicAssignedColor)
                .append("title")
                .text(d => `Date: ${d.date}\nVisitors with recommended assigned desk: ${Math.min(countAssigned(d), d.visitorCount)}`);
        }

        // Upper part (additional visitors)
        svg.selectAll(".flex-desk-rect")
            .data(sortedVisits)
            .enter()
            .append("rect")
            .attr("x", d => x(d.date) || 0)
            .attr("y", d => y(d.visitorCount))
            .attr("width", x.bandwidth())
            .attr("height", d => Math.max(0, height - y(d.visitorCount - countAssigned(d))))
            .attr("fill", d => d.type === 'weekend'
                ? weekendFlexDeskColor
                : d.visitorCount > deskCount
                    ? problemFlexColor
                    : basicFlexColor
            )
            .append("title")
            .text(d => `Date: ${d.date}\nVisitors with recommended flex desk: ${Math.max(0, d.visitorCount - countAssigned(d))}`);


        // Add horizontal line for desk count
        svg.append("line")
            .attr("x1", 0)
            .attr("y1", y(deskCount))
            .attr("x2", width)
            .attr("y2", y(deskCount))
            .attr("stroke", "black")
            .attr("stroke-width", 1)
            .attr("stroke-dasharray", "5,5");

        // Add desk count label on the right side of the chart
        svg.append("text")
            .attr("x", width + 5)
            .attr("y", y(deskCount) + 3)
            .attr("text-anchor", "start") // Left-align the text
            .style("fill", "black")
            .style("font-size", "12px")
            .text(`Desks: ${deskCount}`);
    }, [visits, deskCount, columnWidth, recommendedAssignedDeskCount]);

    return (
        <svg ref={svgRef} style={{maxWidth: '100%', overflow: 'auto'}}></svg>
    );
}
